import { createTheme } from '@mui/material/styles';
import { CSSProperties } from 'react';

declare module '@mui/material/styles' {
  interface Palette {
    brightTeal: React.CSSProperties['color'];
    textFieldBorder: React.CSSProperties['color'];
    inputOutlinedBorder: React.CSSProperties['color'];
    primaryHover: React.CSSProperties['color'];
    primarySurface: React.CSSProperties['color'];
    border: React.CSSProperties['color'];
    secondaryBorder: React.CSSProperties['color'];
    secondarySurface: React.CSSProperties['color'];
    secondaryHover: React.CSSProperties['color'];
    detailsFormBackground: React.CSSProperties['color'];
    utilityIcon: React.CSSProperties['color'];
    icon: Palette['primary'];
    amber: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    blue: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    red: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      A100: string;
      A200: string;
      A400: string;
      A700: string;
    };
    yellow: {
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
    };
    orange: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
    };
    green: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    blueGrey: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    lightBlue: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
      A100: string;
      A200: string;
      A400: string;
      A700: string;
    };
    teal: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
  }

  interface PaletteColor {
    dark: string;
  }

  interface SimplePaletteColorOptions {
    dark?: string;
  }

  interface PaletteOptions {
    brightTeal?: React.CSSProperties['color'];
    textFieldBorder?: React.CSSProperties['color'];
    inputOutlinedBorder?: React.CSSProperties['color'];
    primaryHover?: React.CSSProperties['color'];
    primarySurface?: React.CSSProperties['color'];
    secondaryBorder?: React.CSSProperties['color'];
    secondarySurface?: React.CSSProperties['color'];
    secondaryHover?: React.CSSProperties['color'];
    detailsFormBackground?: React.CSSProperties['color'];
    utilityIcon?: React.CSSProperties['color'];
    icon?: PaletteOptions['primary'];
    amber?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    blue?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    red?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    yellow?: {
      100?: React.CSSProperties['color'];
      200?: React.CSSProperties['color'];
      300?: React.CSSProperties['color'];
      400?: React.CSSProperties['color'];
      500?: React.CSSProperties['color'];
      600?: React.CSSProperties['color'];
      700?: React.CSSProperties['color'];
      800?: React.CSSProperties['color'];
      900?: React.CSSProperties['color'];
    };
    orange?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
    };
    green?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    blueGrey?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    lightBlue?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
    teal?: {
      50: React.CSSProperties['color'];
      100: React.CSSProperties['color'];
      200: React.CSSProperties['color'];
      300: React.CSSProperties['color'];
      400: React.CSSProperties['color'];
      500: React.CSSProperties['color'];
      600: React.CSSProperties['color'];
      700: React.CSSProperties['color'];
      800: React.CSSProperties['color'];
      900: React.CSSProperties['color'];
      A100: React.CSSProperties['color'];
      A200: React.CSSProperties['color'];
      A400: React.CSSProperties['color'];
      A700: React.CSSProperties['color'];
    };
  }

  interface TypographyVariants {
    xxl: CSSProperties;
    xl: CSSProperties;
    l: CSSProperties;
    m: CSSProperties;
    s: CSSProperties;
    xs: CSSProperties;
    xxs: CSSProperties;
    bodyLarge: CSSProperties;
    bodyMedium: CSSProperties;
    bodySmall: CSSProperties;
    metricLarge: CSSProperties;
    metricMedium: CSSProperties;
    metricSmall: CSSProperties;
  }

  interface TypographyVariantsOptions {
    xxl?: CSSProperties;
    xl?: CSSProperties;
    l?: CSSProperties;
    m?: CSSProperties;
    s?: CSSProperties;
    xs?: CSSProperties;
    xxs?: CSSProperties;
    bodyLarge?: CSSProperties;
    bodyMedium?: CSSProperties;
    bodySmall?: CSSProperties;
    metricLarge?: CSSProperties;
    metricMedium?: CSSProperties;
    metricSmall?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xxl: true;
    xl: true;
    l: true;
    m: true;
    s: true;
    xs: true;
    xxs: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
    metricLarge: true;
    metricMedium: true;
    metricSmall: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    green: true;
  }
}

const verifiTheme = createTheme({
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '150%',
      letterSpacing: '0.15px',
    },
    body2: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '143%',
      letterSpacing: '0.17px',

    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '17px',
      lineHeight: '120%',
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '157%',
      letterSpacing: '0.1px',
    },
    overline: {
      fontWeight: 700,
      fontSize: '10px',
      lineHeight: '170%',
      letterSpacing: '5%',
      textTransform: 'uppercase',
    },
    caption: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '166%',
      letterSpacing: '0.4px',
    },
    h1: {
      fontWeight: 300,
      fontSize: '96px',
      lineHeight: '117%',
      letterSpacing: '-1.5px',
    },
    h2: {
      fontWeight: 300,
      fontSize: '60px',
      lineHeight: '120%',
      letterSpacing: '-0.5px'
    },
    h3: {
      fontWeight: 400,
      fontSize: '48px',
      lineHeight: '117%',
      letterSpacing: 0

    },
    h4: {
      fontWeight: 400,
      fontSize: '34px',
      lineHeight: '124%',
      letterSpacing: '0.25px',
    },
    h5: {
      fontWeight: 400,
      fontSize: '24px',
      lineHeight: '133%',
      letterSpacing: 0,
    },
    h6: {
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: '160%',
      letterSpacing: '0.15px',
    },
    xxl: {
      fontWeight: 500,
      fontSize: '32px',
      lineHeight: '36px',
      letterSpacing: 0,
    },
    xl: {
      fontWeight: 500,
      fontSize: '28px',
      lineHeight: '32px',
      letterSpacing: 0,
    },
    l: {
      fontWeight: 500,
      fontSize: '24px',
      lineHeight: '28px',
      letterSpacing: 0,
    },
    m: {
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '24px',
      letterSpacing: 0,
    },
    s: {
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: 0,
    },
    xs: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '18px',
      letterSpacing: 0,
    },
    xxs: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: 0,
    },
    bodyLarge: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: 0,
    },
    bodyMedium: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: 0,
    },
    bodySmall: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: 0,
    },
    metricLarge: {
      fontWeight: 700,
      fontSize: '28px',
      lineHeight: '32px',
      letterSpacing: 0,
    },
    metricMedium: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '28px',
      letterSpacing: 0,
    },
    metricSmall: {
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: 0,
    },
  },
  palette: {
    primary: {
      main: '#2B455E',
      light: '#405A73',
      dark: '#1A344D',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#536D86',
      light: '#617B94',
      dark: '#405A73',
      contrastText: '#FFF',
    },
    text: {
      primary: '#2B455E',
      secondary: '#2B455EBD',
      disabled: '#2B455E61',
    },
    action: {
      active: '#2B455E8F',
      selected: '#EFF7F9'
    },
    background: {
      default: '#FFF'
    },
    error: {
      main: '#D02C44',
      dark: '#C3253C',
      light: '#EE4F67',
    },
    success: {
      main: '#009977',
      dark: '#008C6D',
      light: '#00BB91',
    },
    warning: {
      main: '#FF8F00',
      dark: '#FF6F00',
      light: '#FFA000',
      contrastText: '#000000',

    },
    info: {
      main: '#009FC2', // lightBlue[700]
      dark: '#00596D', // lightBlue[900]
      light: '#00D1FF', // lightBlue[500]
      contrastText: '#FFF',
    },
    amber: {
      50: '#FFF8E1',
      100: '#FFECB3',
      200: '#FFE082',
      300: '#FFD54F',
      400: '#FFCA28',
      500: '#FFC107',
      600: '#FFB300',
      700: '#FFA000',
      800: '#FF8F00',
      900: '#FF6F00',
      A100: '#FFE57F',
      A200: '#FFD740',
      A400: '#FFC400',
      A700: '#FFAB00',
    },
    blue: {
      50: '#E3F2FD',
      100: '#BBDEFB',
      200: '#90CAF9',
      300: '#64B5F6',
      400: '#42A5F5',
      500: '#2196F3',
      600: '#1E88E5',
      700: '#0057B2',
      800: '#1565C0',
      900: '#0D47A1',
      A100: '#82B1FF',
      A200: '#448AFF',
      A400: '#2979FF',
      A700: '#2962FF',
    },
    blueGrey: {
      50: '#F5F7F9',
      100: '#DCE6EF',
      200: '#C4D2E0',
      300: '#93A8BD',
      400: '#758FA8',
      500: '#617B94',
      600: '#536D86',
      700: '#405A73',
      800: '#2B455E',
      900: '#1A344D',
      A100: '#C7D7E6',
      A200: '#AEC2D5',
      A400: '#758FA8',
      A700: '#405A73',
    },
    green: {
      50: '#E8F5E9',
      100: '#C8E6C9',
      200: '#A5D6A7',
      300: '#81C784',
      400: '#66BB6A',
      500: '#4CAF50',
      600: '#43A047',
      700: '#388E3C',
      800: '#2E7D32',
      900: '#1B5E20',
      A100: '#B9F6CA',
      A200: '#69F0AE',
      A400: '#00E676',
      A700: '#00C853',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#F5F5F5',
      A200: '#EEEEEE',
      A400: '#BDBDBD',
      A700: '#616161',
    },
    red: {
      50: '#FEEBEE',
      100: '#FECDD4',
      200: '#EE99A5',
      300: '#E37182',
      400: '#EE4F67',
      500: '#F93B57',
      600: '#E4344E',
      700: '#D02C44',
      800: '#C3253C',
      900: '#B41930',
      A100: '#FF8698',
      A200: '#FC4F69',
      A400: '#FF193B',
      A700: '#CA001E',
    },
    yellow: {
      100: '#FFF9C4',
      200: '#FFF59D',
      300: '#FFF176',
      400: '#FFEE58',
      500: '#FFEB3B',
      600: '#FDD835',
      700: '#FBC02D',
      800: '#F9A825',
      900: '#F57F17'
    },
    orange: {
      50: '#FFF3E0',
      100: '#FFE0B2',
      200: '#FFCC80',
      300: '#FFB74D',
      400: '#FFA726',
      500: '#FF9800',
      600: '#FB8C00',
      700: '#F57C00',
      800: '#EF6C00',
      900: '#CF4900',
    },
    lightBlue: {
      50: '#E8FBFF',
      100: '#D1F7FF',
      200: '#B6F2FF',
      300: '#92EBFF',
      400: '#6FE5FF',
      500: '#00D1FF',
      600: '#00BDE6',
      700: '#009FC2',
      800: '#007791',
      900: '#00596D',
      A100: '#69E4FF',
      A200: '#27D8FF',
      A400: '#04B5DC',
      A700: '#009ABC',
    },
    teal: {
      50: '#C6FFF2',
      100: '#71FFE0',
      200: '#02FAC3',
      300: '#00DDAC',
      400: '#00BB91',
      500: '#009977',
      600: '#008C6D',
      700: '#007B60',
      800: '#006B53',
      900: '#004E3D',
      A100: '#A7FFEB',
      A200: '#63FFDC',
      A400: '#02FAC3',
      A700: '#00C297',
    },
    brightTeal: '#00FAC2',
    icon: {
      main: '#DCE6EF',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          xxl: 'h1',
          xl: 'h2',
          l: 'h3',
          m: 'h4',
          s: 'h5',
          xs: 'h6',
          xxs: 'p',
          bodyLarge: 'p',
          bodyMedium: 'p',
          bodySmall: 'p',
          metricLarge: 'p',
          metricMedium: 'p',
          metricSmall: 'p',
        },
      },
    },
  },
});

// Scoped theme for Operations page
const operationsTheme = createTheme({
  ...verifiTheme,
  palette: (() => {
    // Design colors from Figma design - embedded for operations theme only
    // Get Figma design tokens
    const figmaTokens = {
      'primary/main': '#2b455e',
      'secondary/main': '#536d86',
      'secondary/_states/hover': '#1e3446', // Darker shade for hover states on contained buttons
      'secondary/_states/outlinedBorder': '#536d8680', // Secondary with opacity for outlined border
      'text/disabled': '#2b455e61',
      'common/white_states/main': '#ffffff',
      'grey/50': '#fafafa',
      'divider': '#0000001f',
      'border': 'rgba(0, 0, 0, 0.23)', // Standard Material-UI border
      'textFieldBorder': '#0000001f', // Same as divider
      'inputOutlinedBorder': '#2b455e3b', // From Figma _components/input/outlined/enabledBorder
      'primary/_states/hover': '#2b455e0a',
      'primary/_states/selected': '#2b455e14',
      'action/active': '#2b455e8f',
      'action/hover': '#2b455e0a',
      'action/focus': '#2b455e1f',
      'action/disabled': '#2b455e61',
      'action/disabledBackground': '#2b455e1f',
    };

    const designColors = {
      primary: figmaTokens['primary/main'],
      secondary: figmaTokens['secondary/main'],
      secondaryHover: figmaTokens['secondary/_states/hover'],
      textDisabled: figmaTokens['text/disabled'],
      white: figmaTokens['common/white_states/main'],
      detailsFormBackground: figmaTokens['grey/50'],
      divider: figmaTokens['divider'],
      border: figmaTokens['border'],
      textFieldBorder: figmaTokens['textFieldBorder'],
      inputOutlinedBorder: figmaTokens['inputOutlinedBorder'],
      primaryHover: figmaTokens['primary/_states/hover'],
      primarySurface: figmaTokens['primary/_states/selected'], // Using selected state for surface
      secondaryBorder: 'rgba(83, 109, 134, 0.5)', // Keep custom opacity for now
      secondarySurface: 'rgba(83, 109, 134, 0.04)', // Keep custom opacity for now
      utilityIcon: figmaTokens['action/active']
    };

    return {
      ...verifiTheme.palette,
      primary: {
        main: designColors.primary,
        contrastText: designColors.white,
      },
      secondary: {
        main: designColors.secondary,
        contrastText: designColors.white,
      },
      action: {
        ...verifiTheme.palette.action,
        active: figmaTokens['action/active'],
        hover: figmaTokens['action/hover'],
        focus: figmaTokens['action/focus'],
        disabled: figmaTokens['action/disabled'],
        disabledBackground: figmaTokens['action/disabledBackground'],
      },
      text: {
        ...verifiTheme.palette.text,
        disabled: designColors.textDisabled,
      },
      background: {
        ...verifiTheme.palette.background,
        paper: designColors.white,
      },
      success: {
        main: designColors.primary, // Use dark blue for success messages
        contrastText: designColors.white, // White text for contrast
      },
      divider: designColors.divider,
      textFieldBorder: designColors.textFieldBorder,
      inputOutlinedBorder: designColors.inputOutlinedBorder,
      primaryHover: designColors.primaryHover,
      primarySurface: designColors.primarySurface,
      border: designColors.border,
      secondaryBorder: designColors.secondaryBorder,
      secondarySurface: designColors.secondarySurface,
      secondaryHover: designColors.secondaryHover,
      detailsFormBackground: designColors.detailsFormBackground,
      utilityIcon: designColors.utilityIcon,
    };
  })(),
  components: {
    ...verifiTheme.components,

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px'
        },
        outlined: ({ theme }) => ({
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primarySurface,
          },
          // MUI v7+ removed the combined `outlinedSecondary` override key
          variants: [
            {
              props: { color: 'secondary' },
              style: {
                border: `1px solid ${theme.palette.secondaryBorder}`,
                color: theme.palette.secondary.main,
                fontSize: '13px',
                lineHeight: '22px',
                letterSpacing: '0.46px',
                padding: '4px 10px',
                '&:hover': {
                  backgroundColor: theme.palette.secondarySurface,
                },
              },
            },
          ],
        }),
        contained: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.secondaryHover,
          },
        }),
        sizeSmall: {
          fontWeight: 600,
          letterSpacing: '0.46px',
          textTransform: 'uppercase'
        },
        sizeMedium: {
          fontWeight: 600,
          textTransform: 'uppercase'
        },
        sizeLarge: {
          fontWeight: 600,
          textTransform: 'uppercase'
        },
      }
    },

    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '10px',
          backgroundColor: theme.palette.background.paper,
        }),
      }
    },

    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
        icon: ({ theme }) => ({
          marginLeft: 0,
          marginRight: 0,
          '&[data-testid="LocationOnIcon"]': {
            color: (theme.palette as any).blueGrey[300],
          },
        }),
        label: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },

    MuiSvgIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&[data-testid="LocationOnIcon"]': {
            color: (theme.palette as any).blueGrey[300],
          },
        }),
      },
    },
  }
});

export { verifiTheme, operationsTheme };
export default verifiTheme;
